import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Layers, Mountain, Printer } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button, Chip, Modal, Tabs, TabsList, TabsTrigger, TabsContent } from '@/ui/primitives';
import {
  useImovelSolo,
  useImovelSoilGrids,
  useImovelWosisProfiles,
  useImovelRecomendacao,
  type SoloClasse,
  type SoilGridsResponse,
  type WosisProfile,
  type WosisLayer,
} from '@/modules/clientes/api';

export const Route = createFileRoute('/_authed/imoveis_/$id_/solo')({
  component: SoloMapaRoute,
});

const ORDEM_COLORS: Record<string, string> = {
  LATOSSOLO: '#c9805a',
  ARGISSOLO: '#d4a373',
  NEOSSOLO: '#a3845b',
  CAMBISSOLO: '#9c6b3f',
  GLEISSOLO: '#5d8a8b',
  PLINTOSSOLO: '#b8474e',
  NITOSSOLO: '#7a3b2e',
  ESPODOSSOLO: '#6b7e8f',
  CHERNOSSOLO: '#3a3a3a',
  LUVISSOLO: '#a07856',
  ORGANOSSOLO: '#4a3c2e',
  VERTISSOLO: '#5e4a3a',
};
const ORDEM_FALLBACK = '#8b7a6b';

function colorForOrdem(ordem: string | null | undefined): string {
  if (!ordem) return ORDEM_FALLBACK;
  const key = ordem.toUpperCase().split(/\s+/)[0] ?? '';
  return ORDEM_COLORS[key] ?? ORDEM_FALLBACK;
}

const TILE_LAYERS = {
  satelite: {
    label: 'Satélite (Esri)',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution:
      'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, USDA, USGS, AeroGRID, IGN, GIS User Community',
    maxZoom: 19,
  },
  osm: {
    label: 'Mapa (OpenStreetMap)',
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors',
    maxZoom: 19,
  },
} as const;

type TileKey = keyof typeof TILE_LAYERS;

type SoloTab = 'classes' | 'camadas' | 'perfis' | 'recomendacao';

// Catálogo de camadas — só o que de fato temos com WMS público funcionando.
// SoilGrids (ISRIC) cobre as químicas e físicas; PRODES (INPE TerraBrasilis)
// cobre desmatamento acumulado da Amazônia Legal.
type CamadaSource = 'soilgrids' | 'prodes';

interface CamadaDef {
  prop: string;
  label: string;
  unit: string;
  source: CamadaSource;
  // Pra PRODES: layer name no WMS do TerraBrasilis. Pra SoilGrids: derivado de prop+depth.
  wmsLayer?: string;
  wmsMap?: string;
}
interface CamadaGrupo {
  id: string;
  label: string;
  itens: CamadaDef[];
}

const CAMADAS_GRUPOS: CamadaGrupo[] = [
  {
    id: 'quimicas',
    label: 'Propriedades químicas',
    itens: [
      { prop: 'phh2o', label: 'pH (em H₂O)', unit: '', source: 'soilgrids' },
      { prop: 'soc', label: 'Carbono orgânico', unit: 'g/kg', source: 'soilgrids' },
      { prop: 'nitrogen', label: 'Nitrogênio total', unit: 'g/kg', source: 'soilgrids' },
      { prop: 'cec', label: 'CTC efetiva', unit: 'cmol(c)/kg', source: 'soilgrids' },
    ],
  },
  {
    id: 'fisicas',
    label: 'Propriedades físicas',
    itens: [
      { prop: 'clay', label: 'Argila', unit: '%', source: 'soilgrids' },
      { prop: 'sand', label: 'Areia', unit: '%', source: 'soilgrids' },
      { prop: 'silt', label: 'Silte', unit: '%', source: 'soilgrids' },
      { prop: 'bdod', label: 'Densidade aparente', unit: 'g/cm³', source: 'soilgrids' },
      { prop: 'cfvo', label: 'Conteúdo de pedras', unit: 'cm³/dm³', source: 'soilgrids' },
    ],
  },
  {
    id: 'historico',
    label: 'Histórico (desmatamento PRODES)',
    itens: [
      {
        prop: 'prodes_amz_2024',
        label: 'Desmatamento acumulado até 2024',
        unit: '',
        source: 'prodes',
        wmsMap: 'https://terrabrasilis.dpi.inpe.br/geoserver/wms',
        wmsLayer: 'prodes-amazon-nb:accumulated_deforestation_2007_biome',
      },
    ],
  },
];

const CAMADA_BY_PROP = new Map<string, CamadaDef>(
  CAMADAS_GRUPOS.flatMap((g) => g.itens.map((it) => [it.prop, it])),
);

const PROFUNDIDADES_BR = ['0-20cm', '20-40cm'] as const;

function SoloMapaRoute() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useImovelSolo(id);
  const [tile, setTile] = useState<TileKey>('satelite');
  const [tab, setTab] = useState<SoloTab>('camadas');
  const [sgProp, setSgProp] = useState<string>('soc');
  const [sgDepth, setSgDepth] = useState<string>('0-20cm');
  const [opacity, setOpacity] = useState<number>(0.6);
  const [uncertainty, setUncertainty] = useState<boolean>(false);

  return (
    <div className="absolute inset-0 flex flex-col bg-bg">
      <header className="flex items-center justify-between gap-3 px-4 h-12 border-b border-border bg-surface">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (window.history.length > 1) window.history.back();
              else void navigate({ to: '/clientes' });
            }}
          >
            <ArrowLeft size={14} />
            Voltar
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <Mountain size={14} className="text-text-muted" />
            <span className="font-medium truncate">
              {data?.imovel.denominacao ?? `Imóvel #${id}`}
            </span>
            {data && (
              <span className="text-xs text-text-muted hidden sm:inline truncate">
                · {data.car.municipio}/{data.car.uf}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Layers size={14} className="text-text-muted" />
          <select
            className="select text-xs"
            value={tile}
            onChange={(e) => setTile(e.target.value as TileKey)}
            aria-label="Camada base"
          >
            {(Object.keys(TILE_LAYERS) as TileKey[]).map((k) => (
              <option key={k} value={k}>
                {TILE_LAYERS[k].label}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div className="flex-1 relative">
        {isLoading && (
          <div className="absolute inset-0 grid place-items-center text-text-muted">
            Carregando solo…
          </div>
        )}
        {isError && (
          <div className="absolute inset-0 grid place-items-center text-text-muted">
            Falha ao consultar solo. Verifique se o imóvel tem CAR.
          </div>
        )}
        {data && (
          <MapaSolo
            data={data}
            tile={tile}
            onTileChange={setTile}
            imovelId={id}
            mode={tab}
            onTabChange={setTab}
            sgProp={sgProp}
            onSgPropChange={setSgProp}
            sgDepth={sgDepth}
            onSgDepthChange={setSgDepth}
            opacity={opacity}
            onOpacityChange={setOpacity}
            uncertainty={uncertainty}
            onUncertaintyChange={setUncertainty}
          />
        )}
      </div>
    </div>
  );
}

interface MapaSoloProps {
  data: NonNullable<ReturnType<typeof useImovelSolo>['data']>;
  tile: TileKey;
  onTileChange: (t: TileKey) => void;
  imovelId: string;
  mode: SoloTab;
  onTabChange: (t: SoloTab) => void;
  sgProp: string;
  onSgPropChange: (p: string) => void;
  sgDepth: string;
  onSgDepthChange: (d: string) => void;
  opacity: number;
  onOpacityChange: (v: number) => void;
  uncertainty: boolean;
  onUncertaintyChange: (v: boolean) => void;
}

function MapaSolo({
  data,
  tile,
  onTileChange,
  imovelId,
  mode,
  onTabChange,
  sgProp,
  onSgPropChange,
  sgDepth,
  onSgDepthChange,
  opacity,
  onOpacityChange,
  uncertainty,
  onUncertaintyChange,
}: MapaSoloProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const layersRef = useRef<L.LayerGroup | null>(null);
  const wmsRef = useRef<L.TileLayer.WMS | null>(null);
  const maskRef = useRef<L.Polygon | null>(null);
  const wosisLayerRef = useRef<L.LayerGroup | null>(null);
  const wosisQuery = useImovelWosisProfiles(mode === 'perfis' ? imovelId : null, { radiusKm: 100, limit: 12 });

  // Init map (uma vez)
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const [xmin, ymin, xmax, ymax] = data.car.bbox;
    const map = L.map(containerRef.current, { zoomControl: true }).fitBounds(
      [
        [ymin, xmin],
        [ymax, xmax],
      ],
      { padding: [24, 24] },
    );
    mapRef.current = map;
    layersRef.current = L.layerGroup().addTo(map);
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atualiza tile layer quando troca
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const cfg = TILE_LAYERS[tile];
    if (tileLayerRef.current) tileLayerRef.current.remove();
    tileLayerRef.current = L.tileLayer(cfg.url, {
      attribution: cfg.attribution,
      maxZoom: cfg.maxZoom,
    }).addTo(map);
  }, [tile]);

  // Polígonos RADAM (Classes) e marcadores WoSIS (Perfis) — controlado pelo modo
  useEffect(() => {
    const map = mapRef.current;
    const layers = layersRef.current;
    if (!map || !layers) return;
    layers.clearLayers();

    if (mode === 'classes') {
      for (const c of data.solo.classes) {
        if (!c.geojson) continue;
        const cor = colorForOrdem(c.ordem ?? c.componente);
        const layer = L.geoJSON(c.geojson as GeoJSON.GeoJsonObject, {
          style: { color: cor, weight: 1, fillColor: cor, fillOpacity: 0.6 },
        });
        layer.bindPopup(popupHtml(c, data.solo.total_ha));
        layer.addTo(layers);
      }
    }

    // Polígono CAR sempre por cima — cor de borda contrasta com o que tá embaixo
    if (data.car.geojson) {
      const corCar = mode === 'camadas' ? '#ffffff' : '#1a1a1a';
      L.geoJSON(data.car.geojson as GeoJSON.GeoJsonObject, {
        style: { color: corCar, weight: 2, fill: false, dashArray: '4 3' },
      }).addTo(layers);
    }
  }, [data, mode]);

  // Markers WoSIS — só quando modo='perfis' e dados carregados
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (wosisLayerRef.current) {
      wosisLayerRef.current.remove();
      wosisLayerRef.current = null;
    }
    if (mode !== 'perfis') return;
    const wd = wosisQuery.data;
    if (!wd) return;
    const grp = L.layerGroup();
    for (const p of wd.wosis.profiles) {
      const m = L.circleMarker([p.latitude, p.longitude], {
        radius: 7,
        color: '#1a1a1a',
        weight: 1.5,
        fillColor: '#d97757',
        fillOpacity: 0.9,
      });
      const classes = [p.wrbReferenceSoilGroup, p.usdaOrderName, p.faoMajorGroup]
        .filter(Boolean)
        .join(' / ') || '—';
      const horizDesc = p.layers
        .slice(0, 4)
        .map((l) => `${l.name ?? '?'} ${l.upperDepth}-${l.lowerDepth}cm`)
        .join(' · ');
      m.bindPopup(`
        <div style="font-family:inherit;font-size:12px;min-width:220px">
          <div style="font-weight:600">${escapeHtml(p.profileCode ?? '?')} · ${p.year ?? '—'}</div>
          <div style="opacity:.75;margin:2px 0 6px">${p.distance_km} km do imóvel</div>
          <div><strong>Classe:</strong> ${escapeHtml(classes)}</div>
          <div style="opacity:.75;margin-top:4px;line-height:1.3">${escapeHtml(horizDesc) || 'sem horizontes'}</div>
        </div>
      `);
      m.addTo(grp);
    }
    grp.addTo(map);
    wosisLayerRef.current = grp;
  }, [mode, wosisQuery.data]);

  // WMS — só quando modo='camadas'. Roteia entre SoilGrids (ISRIC) e PRODES (INPE).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (wmsRef.current) {
      wmsRef.current.remove();
      wmsRef.current = null;
    }
    if (mode !== 'camadas') return;
    const camada = CAMADA_BY_PROP.get(sgProp);
    if (!camada) return;

    let url: string;
    let layerName: string;
    let attribution: string;
    if (camada.source === 'soilgrids') {
      const wmsDepth = DEPTH_TO_WMS_NATIVE[sgDepth] ?? sgDepth;
      const valueKind = uncertainty ? 'uncertainty' : 'mean';
      layerName = `${sgProp}_${wmsDepth}_${valueKind}`;
      url = `https://maps.isric.org/mapserv?map=/map/${sgProp}.map`;
      attribution = 'SoilGrids &copy; ISRIC';
    } else if (camada.source === 'prodes' && camada.wmsMap && camada.wmsLayer) {
      url = camada.wmsMap;
      layerName = camada.wmsLayer;
      attribution = 'PRODES &copy; INPE TerraBrasilis';
    } else {
      return;
    }

    const wms = L.tileLayer.wms(url, {
      layers: layerName,
      format: 'image/png',
      transparent: true,
      version: '1.3.0',
      opacity,
      attribution,
    });
    wms.addTo(map);
    wmsRef.current = wms;
  }, [mode, sgProp, sgDepth, opacity, uncertainty]);

  // Mask — escurece tudo fora do polígono CAR pra dar foco visual no imóvel.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (maskRef.current) {
      maskRef.current.remove();
      maskRef.current = null;
    }
    if (mode !== 'camadas') return;
    const carGeo = data.car.geojson as { type: string; coordinates: number[][][] | number[][][][] } | null;
    if (!carGeo) return;
    // Mundo retangular (anel exterior) com CAR como buracos (anéis interiores).
    const world: [number, number][] = [
      [-90, -180], [-90, 180], [90, 180], [90, -180], [-90, -180],
    ];
    const holes: [number, number][][] = [];
    if (carGeo.type === 'Polygon') {
      for (const ring of carGeo.coordinates as number[][][]) {
        holes.push(ring.map((p) => [p[1] ?? 0, p[0] ?? 0] as [number, number]));
      }
    } else if (carGeo.type === 'MultiPolygon') {
      for (const poly of carGeo.coordinates as number[][][][]) {
        for (const ring of poly) {
          holes.push(ring.map((p) => [p[1] ?? 0, p[0] ?? 0] as [number, number]));
        }
      }
    }
    const mask = L.polygon([world, ...holes], {
      stroke: false,
      fillColor: '#000',
      fillOpacity: 0.45,
      interactive: false,
    });
    mask.addTo(map);
    maskRef.current = mask;
  }, [mode, data]);

  return (
    <div className="absolute inset-0 flex">
      <div ref={containerRef} className="flex-1" style={{ minHeight: 0 }} />
      <Legenda
        data={data}
        imovelId={imovelId}
        tab={mode}
        onTabChange={onTabChange}
        sgProp={sgProp}
        onSgPropChange={onSgPropChange}
        sgDepth={sgDepth}
        onSgDepthChange={onSgDepthChange}
        opacity={opacity}
        onOpacityChange={onOpacityChange}
        uncertainty={uncertainty}
        onUncertaintyChange={onUncertaintyChange}
        tile={tile}
        onTileChange={onTileChange}
      />
    </div>
  );
}

function popupHtml(c: SoloClasse, totalHa: number): string {
  const titulo = [c.ordem, c.subordem, c.grande_grupo].filter(Boolean).join(' · ');
  const detalhes = [c.textura, c.relevo].filter(Boolean).join(' · ');
  const pct = totalHa > 0 ? ((c.ha * 100) / totalHa).toFixed(1) : '0';
  return `
    <div style="font-family:inherit;font-size:12px;min-width:200px">
      <div style="font-weight:600;margin-bottom:4px">${escapeHtml(titulo || c.componente || 'Solo')}</div>
      ${detalhes ? `<div style="opacity:.75;margin-bottom:6px">${escapeHtml(detalhes)}</div>` : ''}
      <div><strong>${c.ha.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ha</strong> · ${pct}%</div>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (ch) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch] ?? ch,
  );
}

interface LegendaProps {
  data: MapaSoloProps['data'];
  imovelId: string;
  tab: SoloTab;
  onTabChange: (t: SoloTab) => void;
  sgProp: string;
  onSgPropChange: (p: string) => void;
  sgDepth: string;
  onSgDepthChange: (d: string) => void;
  opacity: number;
  onOpacityChange: (v: number) => void;
  uncertainty: boolean;
  onUncertaintyChange: (v: boolean) => void;
  tile: TileKey;
  onTileChange: (t: TileKey) => void;
}

function Legenda({
  data,
  imovelId,
  tab,
  onTabChange,
  sgProp,
  onSgPropChange,
  sgDepth,
  onSgDepthChange,
  opacity,
  onOpacityChange,
  uncertainty,
  onUncertaintyChange,
  tile,
  onTileChange,
}: LegendaProps) {
  return (
    <aside className="w-80 border-l border-border bg-surface overflow-y-auto p-3 hidden md:flex md:flex-col gap-3">
      <Tabs value={tab} onValueChange={(v) => onTabChange(v as SoloTab)}>
        <TabsList>
          <TabsTrigger value="classes">Classes</TabsTrigger>
          <TabsTrigger value="camadas">Camadas</TabsTrigger>
          <TabsTrigger value="perfis">Perfis</TabsTrigger>
          <TabsTrigger value="recomendacao">Recomenda</TabsTrigger>
        </TabsList>
        <TabsContent value="classes">
          <RadamPanel data={data} />
        </TabsContent>
        <TabsContent value="camadas">
          <CamadasPanel
            imovelId={imovelId}
            sgProp={sgProp}
            onSgPropChange={onSgPropChange}
            sgDepth={sgDepth}
            onSgDepthChange={onSgDepthChange}
            opacity={opacity}
            onOpacityChange={onOpacityChange}
            uncertainty={uncertainty}
            onUncertaintyChange={onUncertaintyChange}
            tile={tile}
            onTileChange={onTileChange}
          />
        </TabsContent>
        <TabsContent value="perfis">
          <PerfisPanel imovelId={imovelId} />
        </TabsContent>
        <TabsContent value="recomendacao">
          <RecomendacaoPanel imovelId={imovelId} />
        </TabsContent>
      </Tabs>
    </aside>
  );
}

function RadamPanel({ data }: { data: MapaSoloProps['data'] }) {
  const ordemAggregada = useMemo(() => {
    const m = new Map<string, { ha: number; pct: number; cor: string }>();
    for (const c of data.solo.classes) {
      const key = (c.ordem || c.componente || '—').toUpperCase().split(/\s+/)[0] ?? '—';
      const cur = m.get(key) ?? { ha: 0, pct: 0, cor: colorForOrdem(c.ordem ?? c.componente) };
      cur.ha += c.ha;
      cur.pct += c.pct;
      m.set(key, cur);
    }
    return Array.from(m.entries()).sort((a, b) => b[1].ha - a[1].ha);
  }, [data]);

  return (
    <div className="space-y-3">
      <div className="text-xs text-text-muted">
        Fonte: IBGE · Pedologia <strong>{data.solo.escala}</strong> · {data.solo.ano_referencia}
      </div>
      <div className="text-xs">
        <span className="text-text-muted">Imóvel:</span>{' '}
        <strong>
          {data.car.area_ha.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ha
        </strong>
      </div>

      <div className="space-y-1.5">
        <div className="text-[11px] uppercase tracking-wider text-text-muted">
          Por ordem
        </div>
        {ordemAggregada.map(([nome, info]) => (
          <div key={nome} className="flex items-center gap-2 text-sm">
            <span
              aria-hidden
              className="h-3 w-3 rounded-sm shrink-0"
              style={{ background: info.cor }}
            />
            <span className="flex-1 truncate">{nome}</span>
            <Chip tone="neutral" className="text-[11px]">
              {info.pct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
            </Chip>
          </div>
        ))}
      </div>

      <div className="space-y-1.5 pt-2 border-t border-border">
        <div className="text-[11px] uppercase tracking-wider text-text-muted">
          Detalhe ({data.solo.classes.length} {data.solo.classes.length === 1 ? 'classe' : 'classes'})
        </div>
        {data.solo.classes.map((c, i) => (
          <div key={i} className="text-xs space-y-0.5">
            <div className="flex items-center gap-2">
              <span
                aria-hidden
                className="h-2.5 w-2.5 rounded-sm shrink-0"
                style={{ background: colorForOrdem(c.ordem ?? c.componente) }}
              />
              <span className="font-medium">
                {[c.ordem, c.subordem].filter(Boolean).join(' ')}
              </span>
            </div>
            {(c.textura || c.relevo) && (
              <div className="pl-4 text-text-muted">
                {[c.textura, c.relevo].filter(Boolean).join(' · ')}
              </div>
            )}
            <div className="pl-4 tabular-nums">
              {c.ha.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ha · {c.pct}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const DEPTH_LABELS: Record<string, string> = {
  '0-5cm': '0–5 cm',
  '5-10cm': '5–10 cm',
  '5-15cm': '5–15 cm',
  '10-20cm': '10–20 cm',
  '15-30cm': '15–30 cm',
  '20-40cm': '20–40 cm',
  '30-60cm': '30–60 cm',
  '60-100cm': '60–100 cm',
  '100-200cm': '100–200 cm',
};

// Profundidades derivadas (não existem no SoilGrids — calculadas no backend
// por média ponderada das nativas que se sobrepõem). O WMS no mapa só
// suporta as 6 nativas, então mapeamos cada derivada pra camada nativa
// que melhor cobre o intervalo.
const DEPTH_TO_WMS_NATIVE: Record<string, string> = {
  '0-20cm': '5-15cm',   // 0-20 sobrepõe 0-5 (5cm) + 5-15 (10cm) + 15-30 (5cm) — escolho 5-15 (média)
  '5-10cm': '5-15cm',   // 5-10 ⊂ 5-15 (cobertura 100%)
  '10-20cm': '15-30cm', // 10-20 sobrepõe 5-15 (5cm) e 15-30 (5cm) — escolha estável
  '20-40cm': '30-60cm', // 20-40 sobrepõe 15-30 (10cm) e 30-60 (10cm) — escolha estável
};

interface CamadasPanelProps {
  imovelId: string;
  sgProp: string;
  onSgPropChange: (p: string) => void;
  sgDepth: string;
  onSgDepthChange: (d: string) => void;
  opacity: number;
  onOpacityChange: (v: number) => void;
  uncertainty: boolean;
  onUncertaintyChange: (v: boolean) => void;
  tile: TileKey;
  onTileChange: (t: TileKey) => void;
}

function CamadasPanel({
  imovelId,
  sgProp,
  onSgPropChange,
  sgDepth,
  onSgDepthChange,
  opacity,
  onOpacityChange,
  uncertainty,
  onUncertaintyChange,
  tile,
  onTileChange,
}: CamadasPanelProps) {
  const { data, isLoading, isError } = useImovelSoilGrids(imovelId);
  const camadaSel = CAMADA_BY_PROP.get(sgProp);
  const wmsDepth = DEPTH_TO_WMS_NATIVE[sgDepth] ?? sgDepth;

  if (isLoading) {
    return <div className="text-text-muted text-sm py-3">Consultando ISRIC SoilGrids…</div>;
  }
  if (isError || !data) {
    return (
      <div className="text-text-muted text-sm py-3">
        SoilGrids indisponível. A API ISRIC pode estar lenta — recarregue em alguns segundos.
      </div>
    );
  }

  const props = data.soilgrids.properties as Record<string, SoilGridsResponse['soilgrids']['properties'][string]>;
  const valueAt = (prop: string, depth: string): number | null => {
    const p = props[prop];
    if (!p) return null;
    const d = p.depths.find((x) => x.depth === depth);
    return d?.value ?? null;
  };
  const valSel = valueAt(sgProp, sgDepth);

  return (
    <div className="space-y-3">
      {/* Header — nome da camada ativa (estilo iSDA) */}
      <div className="space-y-0.5">
        <div className="flex items-center gap-2">
          <span className="h-4 w-4 rounded-sm bg-accent/20 grid place-items-center">
            <Layers size={11} className="text-accent" />
          </span>
          <span className="text-sm font-medium">
            {camadaSel?.label ?? 'Selecione uma camada'}
          </span>
          {camadaSel?.unit ? (
            <span className="text-xs text-text-muted">({camadaSel.unit})</span>
          ) : null}
        </div>
        {camadaSel?.source === 'soilgrids' && (
          <div className="text-xs text-text-muted">
            Valor médio na faixa: {valSel == null ? '—' : <strong>{valSel.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}</strong>}
          </div>
        )}
        {camadaSel?.source === 'prodes' && (
          <div className="text-xs text-text-muted">
            Camada vetorial PRODES — desmatamento acumulado na Amazônia Legal.
            Não há valor pontual amostrado.
          </div>
        )}
      </div>

      {/* Botões de profundidade — só 0-20 e 20-40 (padrão BR) */}
      <div className="flex gap-1.5">
        {PROFUNDIDADES_BR.map((d) => {
          const active = sgDepth === d;
          return (
            <button
              key={d}
              type="button"
              onClick={() => onSgDepthChange(d)}
              className={`px-3 py-1 rounded text-xs font-medium border transition-colors ${
                active
                  ? 'bg-text text-bg border-text'
                  : 'border-border bg-surface hover:bg-surface2 text-text-muted'
              }`}
            >
              {DEPTH_LABELS[d] ?? d}
            </button>
          );
        })}
        <span className="text-[10px] text-text-muted self-center ml-1">
          interpolado · WMS usa {DEPTH_LABELS[wmsDepth] ?? wmsDepth}
        </span>
      </div>

      {/* 4 grupos expansíveis (estilo iSDA) */}
      <div className="space-y-1.5">
        {CAMADAS_GRUPOS.map((g) => (
          <CamadaGrupoAccordion
            key={g.id}
            grupo={g}
            sgProp={sgProp}
            onSgPropChange={onSgPropChange}
            valueAt={(p) => valueAt(p, sgDepth)}
            unit={(p) => CAMADA_BY_PROP.get(p)?.unit ?? ''}
            initiallyOpen={g.itens.some((it) => it.prop === sgProp)}
          />
        ))}
      </div>

      {/* Legenda WMS — só pra SoilGrids (PRODES não expõe legenda PNG estável) */}
      {camadaSel?.source === 'soilgrids' && (
        <div className="space-y-1">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">
            Legenda do mapa
          </div>
          <img
            alt={`Legenda ${sgProp}`}
            src={`https://maps.isric.org/mapserv?map=/map/${sgProp}.map&service=WMS&request=GetLegendGraphic&version=1.3.0&sld_version=1.1.0&format=image/png&layer=${sgProp}_${wmsDepth}_${uncertainty ? 'uncertainty' : 'mean'}`}
            className="bg-white rounded border border-border max-w-full"
            loading="lazy"
          />
        </div>
      )}

      {/* Opacity */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-text-muted">
          <span>Opacidade</span>
          <span>{Math.round(opacity * 100)}%</span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(opacity * 100)}
          onChange={(e) => onOpacityChange(Number(e.target.value) / 100)}
          className="w-full accent-accent"
        />
      </div>

      {/* Uncertainty toggle */}
      <label className="flex items-center justify-between gap-2 text-sm">
        <span className="text-text-muted">Mostrar incerteza (uncertainty)</span>
        <input
          type="checkbox"
          checked={uncertainty}
          onChange={(e) => onUncertaintyChange(e.target.checked)}
          className="accent-accent"
        />
      </label>

      {/* Base layer toggle */}
      <div className="space-y-1">
        <div className="text-[11px] uppercase tracking-wider text-text-muted">Camada base</div>
        <div className="inline-flex rounded-full border border-border overflow-hidden text-xs">
          {(Object.keys(TILE_LAYERS) as TileKey[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => onTileChange(k)}
              className={`px-3 py-1 transition-colors ${
                tile === k
                  ? 'bg-accent text-white'
                  : 'bg-surface text-text-muted hover:bg-surface2'
              }`}
            >
              {k === 'satelite' ? 'Sat' : 'Map'}
            </button>
          ))}
        </div>
      </div>

      <div className="text-[10px] text-text-muted">
        Fonte: ISRIC SoilGrids v2 · {data.soilgrids.pontos_ok}/{data.soilgrids.pontos_amostrados} pontos · resolução {data.soilgrids.escala}
        {data.cache?.hit ? ' · cache' : ''}
      </div>
    </div>
  );
}

function CamadaGrupoAccordion({
  grupo,
  sgProp,
  onSgPropChange,
  valueAt,
  unit,
  initiallyOpen,
}: {
  grupo: CamadaGrupo;
  sgProp: string;
  onSgPropChange: (p: string) => void;
  valueAt: (p: string) => number | null;
  unit: (p: string) => string;
  initiallyOpen?: boolean;
}) {
  const [open, setOpen] = useState<boolean>(!!initiallyOpen);
  const totalAvail = grupo.itens.length;
  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-sm hover:bg-surface2"
      >
        <span className="flex items-center gap-1.5">
          <span className={`text-text-muted transition-transform ${open ? 'rotate-90' : ''}`}>
            ▸
          </span>
          <span className="font-medium text-left">{grupo.label}</span>
        </span>
        <span className="text-[10px] text-text-muted">
          {totalAvail}/{grupo.itens.length}
        </span>
      </button>
      {open && (
        <ul className="border-t border-border divide-y divide-border/60">
          {grupo.itens.map((it) => {
            const isActive = sgProp === it.prop;
            const hasValue = it.source === 'soilgrids';
            const v = hasValue ? valueAt(it.prop) : null;
            return (
              <li key={it.prop}>
                <button
                  type="button"
                  onClick={() => onSgPropChange(it.prop)}
                  className={`w-full flex items-baseline justify-between gap-2 px-2 py-1.5 text-xs text-left transition-colors ${
                    isActive ? 'bg-accent/10 text-text' : 'hover:bg-surface2 text-text'
                  }`}
                >
                  <span className="flex items-center gap-1.5 min-w-0">
                    {isActive && (
                      <span className="h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                    )}
                    <span className="truncate">{it.label}</span>
                  </span>
                  <span className="tabular-nums shrink-0">
                    {hasValue
                      ? v == null
                        ? '—'
                        : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ${unit(it.prop)}`.trim()
                      : <span className="text-[10px] text-text-muted italic">camada</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function PerfisPanel({ imovelId }: { imovelId: string }) {
  const { data, isLoading, isError } = useImovelWosisProfiles(imovelId, { radiusKm: 100, limit: 12 });
  const [expandedId, setExpandedId] = useState<number | null>(null);

  if (isLoading) {
    return <div className="text-text-muted text-sm py-3">Buscando perfis WoSIS…</div>;
  }
  if (isError || !data) {
    return (
      <div className="text-text-muted text-sm py-3">
        WoSIS indisponível. A API ISRIC GraphQL pode estar lenta — recarregue.
      </div>
    );
  }

  const profiles = data.wosis.profiles;
  return (
    <div className="space-y-3">
      <div className="text-xs text-text-muted">
        Fonte: <strong>ISRIC WoSIS</strong> · raio {data.wosis.radius_km} km ·{' '}
        {profiles.length} {profiles.length === 1 ? 'perfil' : 'perfis'} amostrados
        {data.cache?.hit ? <span className="opacity-60"> · cache</span> : null}
      </div>

      {!profiles.length && (
        <div className="text-text-muted text-sm">
          Nenhum perfil amostrado dentro do raio. Tente aumentar para 200 km.
        </div>
      )}

      <ul className="space-y-2">
        {profiles.map((p) => (
          <PerfilRow
            key={p.profileId}
            p={p}
            expanded={expandedId === p.profileId}
            onToggle={() =>
              setExpandedId(expandedId === p.profileId ? null : p.profileId)
            }
          />
        ))}
      </ul>
    </div>
  );
}

function PerfilRow({
  p,
  expanded,
  onToggle,
}: {
  p: WosisProfile;
  expanded: boolean;
  onToggle: () => void;
}) {
  const classes = [p.wrbReferenceSoilGroup, p.usdaOrderName, p.faoMajorGroup]
    .filter(Boolean)
    .join(' / ');
  return (
    <li className="rounded-md border border-border bg-surface">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-2 flex items-start gap-2 hover:bg-surface2"
      >
        <span className="h-2.5 w-2.5 mt-1 rounded-full shrink-0 bg-accent" aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium truncate">
              {p.profileCode ?? `#${p.profileId}`}
              {p.year ? <span className="text-text-muted"> · {p.year}</span> : null}
            </span>
            <Chip tone="neutral" className="text-[11px] shrink-0">
              {p.distance_km} km
            </Chip>
          </div>
          <div className="text-xs text-text-muted">{classes || '— sem classificação'}</div>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-border p-2 space-y-2">
          <PerfilHorizontes layers={p.layers} />
        </div>
      )}
    </li>
  );
}

function PerfilHorizontes({ layers }: { layers: WosisLayer[] }) {
  if (!layers.length) {
    return <div className="text-xs text-text-muted">Sem horizontes registrados.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead className="text-text-muted text-left">
          <tr>
            <th className="pr-2 py-1 font-normal">Hor.</th>
            <th className="pr-2 py-1 font-normal">cm</th>
            <th className="pr-2 py-1 font-normal">pH</th>
            <th className="pr-2 py-1 font-normal">argila %</th>
            <th className="pr-2 py-1 font-normal">SOC g/kg</th>
            <th className="pr-2 py-1 font-normal">CTC</th>
          </tr>
        </thead>
        <tbody>
          {layers.map((l, i) => (
            <tr key={i} className="border-t border-border/60">
              <td className="pr-2 py-1 font-medium">{l.name ?? '—'}</td>
              <td className="pr-2 py-1 tabular-nums">
                {l.upperDepth ?? '?'}–{l.lowerDepth ?? '?'}
              </td>
              <td className="pr-2 py-1 tabular-nums">{l.phaq ?? '—'}</td>
              <td className="pr-2 py-1 tabular-nums">{l.clay ?? '—'}</td>
              <td className="pr-2 py-1 tabular-nums">{l.orgc ?? '—'}</td>
              <td className="pr-2 py-1 tabular-nums">{l.cecph7 ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const APTIDAO_TONE: Record<string, 'ok' | 'accent' | 'warn' | 'danger' | 'neutral'> = {
  apta: 'ok',
  regular: 'accent',
  marginal: 'warn',
  inapta: 'danger',
  sem_dados: 'neutral',
};
const APTIDAO_LABEL: Record<string, string> = {
  apta: 'apta',
  regular: 'regular',
  marginal: 'marginal',
  inapta: 'inapta',
  sem_dados: 'sem dados',
};
const SEV_TONE: Record<string, 'danger' | 'warn' | 'accent'> = {
  alta: 'danger',
  media: 'warn',
  baixa: 'accent',
};

// Culturas com recomendação detalhada implementada nesta onda.
const CULTURAS_COM_RECEITA: Record<string, { meta: string; sacas: number; objetivo: string }> = {
  Soja: { meta: '70 sacas/ha', sacas: 70, objetivo: 'Recomendação com foco em produção 70 sacas por hectare' },
  Milho: { meta: '100 sacas/ha', sacas: 100, objetivo: 'Recomendação com foco em produção 100 sacas por hectare' },
};

function RecomendacaoPanel({ imovelId }: { imovelId: string }) {
  const { data, isLoading, isError, error } = useImovelRecomendacao(imovelId);
  const [culturaSel, setCulturaSel] = useState<string | null>(null);
  if (isLoading) {
    return <div className="text-text-muted text-sm py-3">Calculando recomendação agronômica…</div>;
  }
  if (isError || !data) {
    return (
      <div className="text-text-muted text-sm py-3">
        Recomendação indisponível. {error instanceof Error ? error.message : ''}
      </div>
    );
  }

  const { perfil, limitacoes, intervencoes, culturas, fontes, avisos } = data;

  return (
    <div className="space-y-3">
      {avisos?.length ? (
        <div className="rounded border border-warn/40 bg-warn/5 p-2 text-xs text-text-muted">
          {avisos.map((a, i) => (
            <div key={i}>⚠ {a}</div>
          ))}
        </div>
      ) : null}

      {/* Perfil consolidado */}
      <section className="space-y-1">
        <div className="text-[11px] uppercase tracking-wider text-text-muted">
          Perfil consolidado (0–20 cm)
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <PerfilStat label="pH (H₂O)" value={perfil.ph} digits={1} />
          <PerfilStat label="CTC" value={perfil.ctc} suffix=" cmol/kg" />
          <PerfilStat label="C orgânico" value={perfil.soc} suffix=" g/kg" />
          <PerfilStat label="N total" value={perfil.n} suffix=" g/kg" />
          <PerfilStat label="Argila" value={perfil.argila} suffix="%" digits={0} />
          <PerfilStat label="Areia" value={perfil.areia} suffix="%" digits={0} />
          <PerfilStat label="Silte" value={perfil.silte} suffix="%" digits={0} />
          <PerfilStat label="Densidade" value={perfil.bdod} suffix=" g/cm³" />
        </div>
        {perfil.classe_radam && (
          <div className="text-xs text-text-muted pt-1 leading-snug">
            <strong>Classe RADAM:</strong> {perfil.classe_radam.ordem}
            {perfil.classe_radam.subordem ? ` ${perfil.classe_radam.subordem}` : ''}
            {perfil.classe_radam.grande_grupo ? ` ${perfil.classe_radam.grande_grupo}` : ''}
            {perfil.classe_radam.textura ? ` · ${perfil.classe_radam.textura}` : ''}
          </div>
        )}
        {perfil.perfil_wosis_proximo && (
          <div className="text-xs text-text-muted">
            <strong>Perfil WoSIS próximo:</strong> {perfil.perfil_wosis_proximo.codigo}
            {perfil.perfil_wosis_proximo.ano ? ` (${perfil.perfil_wosis_proximo.ano})` : ''} ·{' '}
            {perfil.perfil_wosis_proximo.distancia_km} km · {perfil.perfil_wosis_proximo.horizontes} horizontes
          </div>
        )}
      </section>

      {/* Limitações */}
      {limitacoes.length > 0 && (
        <section className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">
            Limitações detectadas
          </div>
          <ul className="space-y-1.5">
            {limitacoes.map((l, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-xs rounded border border-border bg-surface p-2"
              >
                <Chip tone={SEV_TONE[l.sev] ?? 'neutral'} className="text-[10px] shrink-0 uppercase">
                  {l.sev}
                </Chip>
                <span className="text-text">{l.desc}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Intervenções */}
      {intervencoes.length > 0 && (
        <section className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">
            Intervenções sugeridas
          </div>
          <ul className="space-y-1.5">
            {intervencoes.map((iv, i) => (
              <li key={i} className="text-xs rounded border border-border bg-surface p-2 space-y-0.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{iv.acao}</span>
                  {iv.dose && (
                    <Chip tone="accent" className="text-[10px]">
                      {iv.dose}
                    </Chip>
                  )}
                </div>
                <div className="text-text-muted leading-snug">{iv.justificativa}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Aptidão por cultura */}
      <section className="space-y-1.5">
        <div className="text-[11px] uppercase tracking-wider text-text-muted">
          Aptidão por cultura
        </div>
        <div className="text-[10px] text-text-muted">
          Clique numa cultura com receita (●) pra ver recomendação detalhada.
        </div>
        <ul className="space-y-1">
          {culturas.map((c) => {
            const temReceita = !!CULTURAS_COM_RECEITA[c.nome];
            return (
              <li key={c.nome}>
                <button
                  type="button"
                  disabled={!temReceita}
                  onClick={() => temReceita && setCulturaSel(c.nome)}
                  className={`w-full flex items-center gap-2 text-xs py-0.5 px-1 rounded transition-colors ${
                    temReceita ? 'hover:bg-surface2 cursor-pointer' : 'cursor-default'
                  }`}
                  title={temReceita ? `Ver recomendação ${CULTURAS_COM_RECEITA[c.nome]!.meta}` : 'Receita detalhada nas próximas ondas'}
                >
                  <span className="w-28 truncate text-left flex items-center gap-1">
                    {temReceita && <span className="h-1.5 w-1.5 rounded-full bg-accent shrink-0" />}
                    <span className="truncate">{c.nome}</span>
                  </span>
                  <div className="flex-1 h-2 rounded-sm bg-surface2 overflow-hidden">
                    {c.score_pct != null && (
                      <div
                        className="h-full"
                        style={{
                          width: `${c.score_pct}%`,
                          background:
                            c.aptidao === 'apta' ? '#4a8a5e' :
                            c.aptidao === 'regular' ? '#d97757' :
                            c.aptidao === 'marginal' ? '#d4a73a' :
                            c.aptidao === 'inapta' ? '#a64545' :
                            '#999',
                        }}
                      />
                    )}
                  </div>
                  <span className="w-10 text-right tabular-nums shrink-0">
                    {c.score_pct == null ? '—' : `${c.score_pct}%`}
                  </span>
                  <Chip
                    tone={APTIDAO_TONE[c.aptidao] ?? 'neutral'}
                    className="text-[10px] w-20 shrink-0 justify-center"
                  >
                    {APTIDAO_LABEL[c.aptidao] ?? c.aptidao}
                  </Chip>
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      {culturaSel && (
        <ReceitaCulturaModal
          cultura={culturaSel}
          perfil={perfil}
          onClose={() => setCulturaSel(null)}
        />
      )}

      {/* Fontes */}
      <section className="text-[10px] text-text-muted leading-snug pt-2 border-t border-border">
        <strong>Fontes cruzadas:</strong>
        <ul className="space-y-0.5 mt-0.5">
          {fontes.soilgrids && <li>• SoilGrids ISRIC v2 (cache local)</li>}
          {fontes.radam && <li>• {fontes.radam}</li>}
          {fontes.wosis && <li>• {fontes.wosis}</li>}
        </ul>
        <div className="mt-1 italic">
          Modelo orientativo baseado em literatura Embrapa. Não substitui análise
          de solo amostrada por laboratório credenciado.
        </div>
      </section>
    </div>
  );
}

function PerfilStat({
  label,
  value,
  suffix,
  digits = 2,
}: {
  label: string;
  value: number | null;
  suffix?: string;
  digits?: number;
}) {
  return (
    <div className="flex justify-between gap-2 min-w-0">
      <span className="text-text-muted truncate">{label}</span>
      <span className="tabular-nums font-medium shrink-0">
        {value == null ? '—' : value.toFixed(digits)}
        {value != null && suffix ? <span className="text-text-muted">{suffix}</span> : null}
      </span>
    </div>
  );
}

// ─── Receita detalhada por cultura (Soja, Milho) ──────────────────────
type PerfilSolo = NonNullable<ReturnType<typeof useImovelRecomendacao>['data']>['perfil'];

// V1 (saturação por bases atual) estimada por pH em água — proxy linear
// quando análise de solo amostral não está disponível.
function estimaV1FromPh(ph: number | null): number | null {
  if (ph == null) return null;
  return Math.max(5, Math.min(80, (ph - 3.5) * 25));
}

// Necessidade de calagem (Manual de Calagem RS/SC adaptado): NC = (V2-V1)*CTC/PRNT.
// CTC em cmol(c)/kg ≈ ml/100g; conversão final pra t/ha assumindo 0-20cm e densidade 1.2.
function calculaCalagem(perfil: PerfilSolo, v2Alvo: number) {
  const v1 = estimaV1FromPh(perfil.ph);
  if (v1 == null || perfil.ctc == null) return null;
  if (v1 >= v2Alvo) return { dose_t_ha: 0, v1: Math.round(v1), v2: v2Alvo, motivo: 'V atual já alcança meta' };
  const prnt = 90; // calcário dolomítico comercial
  // Fórmula clássica devolve t/ha pra incorporação 0-20 cm
  const nc = ((v2Alvo - v1) * perfil.ctc) / prnt;
  return {
    dose_t_ha: Math.max(0, Number(nc.toFixed(1))),
    v1: Math.round(v1),
    v2: v2Alvo,
    motivo: 'NC = (V₂ − V₁) × CTC ÷ PRNT, PRNT 90%',
  };
}

interface ReceitaCultura {
  meta: string;
  ciclo: string;
  npk_base: { N: string; P2O5: string; K2O: string; obs: string };
  cobertura?: { N: string; obs: string };
  manejo: string[];
  cuidados: string[];
}

function buildReceitaSoja(perfil: PerfilSolo): ReceitaCultura {
  const argila = perfil.argila;
  const p2o5 =
    argila == null ? '90 kg/ha' :
    argila > 60 ? '120 kg/ha (solo argiloso prende muito P)' :
    argila > 35 ? '90 kg/ha' :
    '70 kg/ha';
  return {
    meta: '70 sacas/ha (4.200 kg/ha)',
    ciclo: 'Variedade indeterminada precoce/médio · GMR 8.0–9.0 (RO bioma Amazônia)',
    npk_base: {
      N: '0–20 kg/ha (start)',
      P2O5: p2o5,
      K2O: '80–100 kg/ha',
      obs: 'Adubação no plantio. Soja não exige N porque a fixação biológica supre — inoculação obrigatória.',
    },
    manejo: [
      'Inoculação com Bradyrhizobium japonicum no plantio (mínimo 600 mil cel/semente)',
      'Co-inoculação com Azospirillum brasilense aumenta produtividade ~5%',
      'População 12–14 plantas/m linear, espaçamento 0,45–0,50 m',
      'Tratamento de sementes: fungicida + inseticida + Mo + Co',
      'Aplicar Mn foliar V4–V6 se pH > 6,2 (deficiência induzida)',
    ],
    cuidados: [
      'Atenção a percevejo marrom e lagarta-falsa-medideira — monitorar V5 em diante',
      'Ferrugem asiática: 2–3 fungicidas preventivos R1–R3, rotação de princípios ativos',
      'Soja sucessão milho-safrinha valoriza adubação residual de P/K',
    ],
  };
}

function buildReceitaMilho(perfil: PerfilSolo): ReceitaCultura {
  const argila = perfil.argila;
  const p2o5 =
    argila == null ? '100 kg/ha' :
    argila > 60 ? '130 kg/ha' :
    argila > 35 ? '100 kg/ha' :
    '80 kg/ha';
  return {
    meta: '100 sacas/ha (6.000 kg/ha)',
    ciclo: 'Híbrido simples ciclo médio (140–155 dias) · alta tecnologia',
    npk_base: {
      N: '30 kg/ha',
      P2O5: p2o5,
      K2O: '90–120 kg/ha',
      obs: 'Adubação no plantio. Milho responde linear a N — parcelar pra cobertura.',
    },
    cobertura: {
      N: '120–150 kg/ha (parcelado V4–V6 e V8)',
      obs: 'N total 150–180 kg/ha pra meta 100 sacas. Ureia incorporada ou nitrato de amônio.',
    },
    manejo: [
      'Densidade 60–70 mil plantas/ha, espaçamento 0,45–0,50 m',
      'Tratamento de sementes: fungicida + inseticida + Zn (1–2 g/kg semente)',
      'Adubação foliar Zn V4 se solo arenoso ou pH > 6,2',
      'Manejo de plantas daninhas em pré e pós-emergência (atrazina + tembotrione)',
    ],
    cuidados: [
      'Cigarrinha-do-milho (vetor de molicutes) — tratamento de sementes + monitoramento V0–V8',
      'Lagarta-do-cartucho — controle biológico (Bt) ou inseticida em V4–V8',
      'Helmintosporiose e ferrugem polissora — fungicida em VT/R1',
      'Em RO, 2ª safra (safrinha) exige semeadura até fim de janeiro pra escapar de seca',
    ],
  };
}

function ReceitaCulturaModal({
  cultura,
  perfil,
  onClose,
}: {
  cultura: string;
  perfil: PerfilSolo;
  onClose: () => void;
}) {
  const cfg = CULTURAS_COM_RECEITA[cultura];
  const [mapaImg, setMapaImg] = useState<string | null>(null);
  const [capturando, setCapturando] = useState<boolean>(true);

  // Captura o mapa Leaflet em runtime via html2canvas — embute como imagem no modal
  // pra documentar a área no PDF impresso. Roda 1x ao abrir modal.
  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        const target = document.querySelector('.leaflet-container') as HTMLElement | null;
        if (!target) {
          setCapturando(false);
          return;
        }
        // Espera 250ms pra tiles terminarem de carregar
        await new Promise((r) => setTimeout(r, 250));
        const html2canvas = (await import('html2canvas')).default;
        const canvas = await html2canvas(target, {
          useCORS: true,
          allowTaint: false,
          backgroundColor: null,
          logging: false,
          scale: window.devicePixelRatio > 1 ? 1.5 : 1,
        });
        if (canceled) return;
        setMapaImg(canvas.toDataURL('image/png'));
      } catch (e) {
        console.warn('[receita] captura do mapa falhou:', e);
      } finally {
        if (!canceled) setCapturando(false);
      }
    })();
    return () => {
      canceled = true;
    };
  }, []);

  if (!cfg) return null;
  const receita = cultura === 'Soja' ? buildReceitaSoja(perfil) : buildReceitaMilho(perfil);
  const v2Alvo = cultura === 'Milho' ? 65 : 60;
  const calagem = calculaCalagem(perfil, v2Alvo);

  return (
    <Modal
      open={true}
      onOpenChange={(v) => !v && onClose()}
      title={`${cultura} · receita técnica`}
      size="lg"
      headerActions={
        <button
          type="button"
          onClick={() => window.print()}
          className="receita-no-print inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-xs hover:bg-surface2"
          aria-label="Imprimir / Salvar como PDF"
        >
          <Printer size={13} />
          PDF
        </button>
      }
    >
      <style>{`
        @media print {
          @page { size: A4; margin: 1.4cm 1.6cm; }
          html, body { background: #fff !important; color: #1a1a1a !important; }
          body > div:not(:has([data-print-receita])) { display: none !important; }
          [role="dialog"] {
            position: static !important;
            inset: auto !important;
            transform: none !important;
            max-height: none !important;
            max-width: none !important;
            width: 100% !important;
            box-shadow: none !important;
            border: none !important;
            background: #fff !important;
            overflow: visible !important;
          }
          .receita-no-print { display: none !important; }
          [data-print-receita] { color: #1a1a1a !important; }
          [data-print-receita] * { color: inherit !important; }
          [data-print-receita] section { break-inside: avoid; }
        }
      `}</style>
      <div data-print-receita className="space-y-4 text-sm">
        <div className="hidden print:block mb-3">
          <div className="text-xs text-text-muted">Hub Ruralplan · Recomendação técnica</div>
          <div className="text-lg font-semibold mt-0.5">{cultura} — {receita.meta}</div>
          <div className="text-xs text-text-muted">{cfg.objetivo}</div>
        </div>
        <div className="rounded-md border border-accent/40 bg-accent/5 p-3">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wider text-text-muted">Foco</span>
            <strong>{cfg.objetivo}</strong>
          </div>
          <div className="text-xs text-text-muted mt-1">
            Meta de produtividade: <strong>{receita.meta}</strong>
          </div>
        </div>

        <Section title="Mapa da área">
          {capturando ? (
            <div className="text-xs text-text-muted py-3 text-center">Capturando mapa…</div>
          ) : mapaImg ? (
            <img
              src={mapaImg}
              alt="Mapa do imóvel com a camada selecionada"
              className="w-full rounded border border-border"
            />
          ) : (
            <div className="text-xs text-text-muted py-2">Não foi possível capturar o mapa.</div>
          )}
        </Section>

        <Section title="1 · Diagnóstico do solo (0–20 cm)">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <PerfilStat label="pH (H₂O)" value={perfil.ph} digits={1} />
            <PerfilStat label="CTC" value={perfil.ctc} suffix=" cmol/kg" />
            <PerfilStat label="C orgânico" value={perfil.soc} suffix=" g/kg" />
            <PerfilStat label="Argila" value={perfil.argila} suffix="%" digits={0} />
            <PerfilStat label="Areia" value={perfil.areia} suffix="%" digits={0} />
            <PerfilStat label="Silte" value={perfil.silte} suffix="%" digits={0} />
          </div>
          {perfil.classe_radam?.ordem && (
            <div className="text-xs text-text-muted mt-2">
              Classe RADAM: <strong>{perfil.classe_radam.ordem} {perfil.classe_radam.subordem ?? ''}</strong>
              {perfil.classe_radam.grande_grupo ? ` ${perfil.classe_radam.grande_grupo}` : ''}
            </div>
          )}
        </Section>

        <Section title="2 · Correção da acidez (calagem)">
          {calagem == null ? (
            <div className="text-xs text-text-muted">
              Sem dados suficientes (pH ou CTC ausentes) pra calcular dose. Faça análise de solo amostral.
            </div>
          ) : calagem.dose_t_ha === 0 ? (
            <div className="text-xs text-ok">
              ✓ V atual ({calagem.v1}%) já alcança a meta de V₂ = {calagem.v2}%. Calagem não necessária.
            </div>
          ) : (
            <div className="space-y-1 text-xs">
              <div>
                Dose recomendada: <strong>{calagem.dose_t_ha} t/ha</strong> de calcário dolomítico
                (PRNT 90%), incorporação 0–20 cm, antes do plantio (60–90 dias).
              </div>
              <div className="text-text-muted">
                Cálculo: V₂ alvo {calagem.v2}% · V₁ estimado {calagem.v1}% (proxy do pH) · CTC {perfil.ctc?.toFixed(1)} cmol/kg.
              </div>
              <div className="text-text-muted italic">
                {calagem.motivo}. Análise de solo amostral substituirá a estimativa de V₁.
              </div>
            </div>
          )}
        </Section>

        <Section title="3 · Adubação de plantio (NPK)">
          <div className="grid grid-cols-3 gap-2 text-xs">
            <NpkBox label="N" value={receita.npk_base.N} />
            <NpkBox label="P₂O₅" value={receita.npk_base.P2O5} />
            <NpkBox label="K₂O" value={receita.npk_base.K2O} />
          </div>
          <div className="text-xs text-text-muted mt-2 leading-snug">{receita.npk_base.obs}</div>
        </Section>

        {receita.cobertura && (
          <Section title="4 · Cobertura nitrogenada">
            <div className="text-xs">
              <strong>{receita.cobertura.N}</strong>
            </div>
            <div className="text-xs text-text-muted mt-1 leading-snug">{receita.cobertura.obs}</div>
          </Section>
        )}

        <Section title={`${receita.cobertura ? '5' : '4'} · Manejo / cultivar`}>
          <div className="text-xs text-text-muted mb-1.5">{receita.ciclo}</div>
          <ul className="text-xs space-y-1">
            {receita.manejo.map((m, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-accent">•</span>
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title={`${receita.cobertura ? '6' : '5'} · Cuidados / fitossanidade`}>
          <ul className="text-xs space-y-1">
            {receita.cuidados.map((c, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-warn">!</span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </Section>

        <div className="text-[10px] text-text-muted italic border-t border-border pt-2">
          Recomendação orientativa baseada em literatura Embrapa (Soja, Milho, Cerrados) e Manual de
          Calagem RS/SC. <strong>Não substitui análise de solo amostrada por laboratório credenciado
          nem assistência técnica local.</strong>
        </div>
      </div>
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="text-xs uppercase tracking-wider text-text-muted">{title}</h3>
      <div className="rounded-md border border-border bg-surface2/50 p-2.5">{children}</div>
    </section>
  );
}

function NpkBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-border bg-surface p-2 text-center">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className="text-sm font-medium mt-0.5">{value}</div>
    </div>
  );
}
